import { ComponentType } from '@angular/cdk/portal'
import { ChangeDetectorRef, Component, OnInit, TemplateRef } from '@angular/core'
import { MatDialogRef } from '@angular/material/dialog'
import { ActivatedRoute } from '@angular/router'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { filter } from 'rxjs/operators'
import { Employee } from '../../../api/models/employee.model'
import {
    Timesheet,
    TimesheetGenCriteria,
    TimesheetSearchCriteria,
    TimesheetState,
    toText
} from '../../../api/models/timesheet.model'
import { TimesheetService } from '../../../api/services/timesheet.service'
import { AuthFacade } from '../../auth/auth.facade'
import { NavigationPaths } from '../../common/components/hierarch-menu/hierarchy-menu.component'
import { Filter } from '../../common/model/hierarchy-menu.model'
import { DialogService } from '../../common/services/dialog.service'
import { EmployeeFilterService } from '../../common/services/employee-filter.service'
import { LoaderService } from '../../common/services/loader.service'
import { SnackbarService } from '../../common/services/snackbar.service'
import { TimekeeperService } from '../../common/services/time-keeper.service'
import { notUndefined } from '../../common/utils/conditional-assert'
import { Utils } from '../../common/utils/utils'
import { UnsubscribeOnDestroyAdapter } from '../../shared/adapter/unsubscribe-on-destroy-adapter'
import { IDateable } from '../components/date-selector/idateable.interface'
import { RoleEnum } from '../constants/roles.constant'
import { TimesheetsNotInvoiceDialogComponent } from './timesheets-not-invoice-dialog/timesheets-not-invoice-dialog.component'
import {
    TimesheetsResetDialogComponent,
    TimesheetsResetDialogResult
} from './timesheets-reset-dialog/timesheets-reset-dialog.component'

export interface TimesheetContainer extends Timesheet {
    name: string
    isExpanded: boolean
    parent: TimesheetContainer
    toInvoice: boolean
    hoursToCharge: string
    hoursNoCharge: string
    hoursTotal: string
    daysToCharge: string
    daysNoCharge: string
    daysTotal: string
    indentLvl: number
    isRoot: boolean
    children: Array<TimesheetContainer>
}

@Component({
    selector: 'ppw-app-timesheets',
    templateUrl: './timesheets.component.html',
    styleUrls: ['./timesheets.component.scss']
})
export class TimesheetsComponent extends UnsubscribeOnDestroyAdapter implements OnInit {
    trackBy = Utils.trackByIndex
    datasource$: Observable<Array<TimesheetContainer> | undefined>
    displayedColumns: Array<string> = ['name', 'sendInvoice', 'status', 'HTC', 'HNC', 'HTT', 'DTC', 'DNC', 'DTT']
    selectedTimesheets: Array<TimesheetContainer> = new Array<TimesheetContainer>()
    isViewByTag!: boolean
    isAuthForAllButRestricted!: boolean
    pageTitle = 'Timesheets'
    viewTimesheet = false
    readonly datasourceSubject: BehaviorSubject<Array<TimesheetContainer> | undefined> = new BehaviorSubject<
        Array<TimesheetContainer> | undefined
    >(undefined)
    scrollToSelectedTimesheet$!: Observable<void>
    scrollToSelectedTimesheetSubject: Subject<void> = new Subject()
    latestDatasource?: Array<TimesheetContainer>
    dateSelectorConfig = {
        icon: 'search',
        iconTitle: 'Fetch timesheets',
        page: NavigationPaths.timesheets
    }
    private filter!: Filter<TimesheetGenCriteria>
    private readonly prefix = 'TimesheetsComponent'
    private isOnlyNotInvoiced?: boolean
    get hasUnapprovedInSelection(): boolean {
        return this.selectedTimesheets.some((container: TimesheetContainer) => container.state === TimesheetState.TEMP)
    }

    get hasUninvoicedInSelection(): boolean {
        return this.selectedTimesheets.some((container: TimesheetContainer) => !container.invoiced)
    }

    get hasToBeInvoicedInSelection(): boolean {
        return this.selectedTimesheets.some((container: TimesheetContainer) => container.toInvoice)
    }

    get hasDoNotInvoiceInSelection(): boolean {
        return this.selectedTimesheets.some((container: TimesheetContainer) => !container.toInvoice)
    }

    get hasMultiClientsInMultiSelect(): boolean {
        if (this.selectedTimesheets && this.selectedTimesheets.length > 1) {
            const firstClient = this.selectedTimesheets[0].timesheetProject.clientName

            return this.selectedTimesheets.some(
                (container: TimesheetContainer) => container.timesheetProject.clientName !== firstClient
            )
        }

        return false
    }

    constructor(
        private readonly timesheetService: TimesheetService,
        private readonly timekeeperService: TimekeeperService,
        private readonly loaderService: LoaderService,
        private readonly snackbarService: SnackbarService,
        private readonly route: ActivatedRoute,
        private readonly dialogService: DialogService,
        private readonly auth: AuthFacade,
        private readonly employeeFilterService: EmployeeFilterService,
        private changeDetector: ChangeDetectorRef
    ) {
        super()
        this.subscriptions.add(
            this.route.data.subscribe((data) => {
                const lastEmployeeFilter = this.employeeFilterService.lastSelectedEmployee
                this.filter = {
                    searchParams: this.getTimesheetGenCriteria(),
                    groupBy: data.groupBy,
                    employeeId: lastEmployeeFilter ? lastEmployeeFilter.id : undefined
                }
                this.isViewByTag = this.filter.groupBy[0] === 'tag'
                this.isOnlyNotInvoiced = data.isOnlyNotInvoiced
            })
        )
        this.datasource$ = this.datasourceSubject.asObservable()
        this.scrollToSelectedTimesheet$ = this.scrollToSelectedTimesheetSubject.asObservable()
    }

    async ngOnInit(): Promise<void> {
        void this.fetchTimesheets()
        this.startEmployeeFilterSubscription()
        this.isAuthForAllButRestricted = await this.auth.hasRequiredRole(RoleEnum.ALL_BUT_RESTRICTED)
    }

    setSelectedTimesheets(selectedTimesheets: Array<TimesheetContainer>): void {
        this.selectedTimesheets = selectedTimesheets
    }

    setLatestDatasource(latestDatasource: Array<TimesheetContainer>): void {
        this.latestDatasource = latestDatasource
    }

    async fetchTimesheetWithDates(criteria: IDateable): Promise<void> {
        this.filter.searchParams.month = criteria.month
        this.filter.searchParams.year = notUndefined(criteria.year)
        await this.fetchTimesheets()
    }

    toggleTimesheet(): void {
        this.viewTimesheet = !this.viewTimesheet
        if (!this.viewTimesheet) {
            this.changeDetector.detectChanges() // manually detect changes so our child component knows of the viewChildren instantly
            this.scrollToSelectedTimesheetSubject.next()
        }
    }

    async approveSelectedTimesheets(): Promise<void> {
        this.loaderService.startLoading()
        const allPromises: Array<Promise<Timesheet>> = []
        const nonApprovedTimesheets = this.selectedTimesheets.filter((t) => t.state === TimesheetState.TEMP)
        nonApprovedTimesheets.forEach((timesheet) => {
            allPromises.push(this.approveAndMapId(timesheet))
        })
        try {
            await Promise.all<Timesheet>(allPromises)
            this.selectedTimesheets.forEach((selectedTimesheet) => {
                const indexToReplace = notUndefined(this.latestDatasource).findIndex(
                    (timesheet) => timesheet === selectedTimesheet
                )
                selectedTimesheet.state = TimesheetState.APPROVED
                notUndefined(this.latestDatasource).splice(indexToReplace, 1, selectedTimesheet)
                this.replaceTimesheetAsChildInParent(selectedTimesheet)
            })
            this.datasourceSubject.next(notUndefined(this.latestDatasource))
            this.toggleTimesheet()
            this.snackbarService.promtSnackBar('Selected timesheet(s) have been APPROVED')
        } catch (err) {
            this.snackbarService.handleError(this.prefix, err)
        } finally {
            this.loaderService.stopLoading()
        }
    }

    resetTimesheet(): void {
        this.subscriptions.add(
            this.showConfirmDialog(TimesheetsResetDialogComponent)
                .afterClosed()
                .subscribe(async (result: TimesheetsResetDialogResult) => {
                    if (result) {
                        this.loaderService.startLoading()
                        if (this.selectedTimesheets.length === 1) {
                            const selectedTimesheet = this.selectedTimesheets[0]
                            try {
                                await this.timesheetService.deleteTimesheet(
                                    selectedTimesheet,
                                    result.timecardResetStatus
                                )
                                await this.fetchTimesheetsByLocalstorageCriteria()
                                this.toggleTimesheet()
                                this.snackbarService.promtSnackBar('Selected timesheet has been reset')
                            } catch (err) {
                                this.snackbarService.handleError(this.prefix, err)
                            } finally {
                                this.loaderService.stopLoading()
                            }
                        }
                    } // else do nothing, did not confirm the dialog
                })
        )
    }

    doNotInvoiceSelectedTimesheets(): void {
        this.subscriptions.add(
            this.showConfirmDialog(TimesheetsNotInvoiceDialogComponent)
                .afterClosed()
                .subscribe(async (confirmed: boolean) => {
                    if (confirmed) {
                        this.loaderService.startLoading()
                        const allPromises: Array<Promise<Timesheet>> = []
                        this.selectedTimesheets.forEach((timesheet) => {
                            allPromises.push(this.timesheetService.setTimesheetToDoNotInvoice(timesheet))
                        })
                        try {
                            await Promise.all<Timesheet>(allPromises)
                            this.selectedTimesheets.forEach((timesheet) => {
                                timesheet.toInvoice = false
                                this.replaceTimesheetAsChildInParent(timesheet)
                            })
                            this.snackbarService.promtSnackBar('Selected timesheet(s) have been set to DO NOT INVOICE')
                        } catch (err) {
                            this.snackbarService.handleError(this.prefix, err)
                        } finally {
                            this.loaderService.stopLoading()
                        }
                    }
                })
        )
    }

    async invoiceSelectedTimesheets(): Promise<void> {
        this.loaderService.startLoading()
        const allPromises: Array<Promise<Timesheet>> = []
        this.selectedTimesheets.forEach((timesheet) => {
            allPromises.push(this.timesheetService.invoiceTimesheet(timesheet))
        })
        try {
            await Promise.all<Timesheet>(allPromises)
            this.selectedTimesheets.forEach((timesheet) => {
                timesheet.invoiced = true
                timesheet.state = TimesheetState.INVOICED
                this.replaceTimesheetAsChildInParent(timesheet)
            })
            this.datasourceSubject.next(notUndefined(this.latestDatasource))
            this.snackbarService.promtSnackBar('Selected timesheet(s) have been set to INVOICED')
        } catch (err) {
            this.snackbarService.handleError(this.prefix, err)
        } finally {
            this.loaderService.stopLoading()
        }
    }

    calculateTotals(): void {
        const totalSecondsToInvoice = this.selectedTimesheets.reduce(
            (total, timesheet) => total + timesheet.secondsToInvoice,
            0
        )
        const totalSecondsNoInvoice = this.selectedTimesheets.reduce(
            (total, timesheet) => total + timesheet.secondsNoInvoice,
            0
        )

        this.snackbarService.promtSnackBar(
            `[ # of timesheets selected = ${this.selectedTimesheets.length} ]-----
            [ selected total to invoice = ${Utils.secondsAsHours(totalSecondsToInvoice)} Hours =
             ${Utils.secondsAsWorkingDay(totalSecondsToInvoice)} Days =
             ${Utils.secondsAsWholeWorkingDaysAndHours(totalSecondsToInvoice)} ]
             ]-----[ selected total no invoice = ${Utils.secondsAsHours(totalSecondsNoInvoice)} Hours =
             ${Utils.secondsAsWorkingDay(totalSecondsNoInvoice)} Days =
             ${Utils.secondsAsWholeWorkingDaysAndHours(totalSecondsNoInvoice)} ]`,
            undefined,
            150000,
            'center',
            'full-width'
        )
    }

    printTimesheets(): void {
        window.print()
    }

    private replaceTimesheetAsChildInParent(timesheet: TimesheetContainer): void {
        if (timesheet.parent !== undefined) {
            const cloneTimesheet: TimesheetContainer = {
                ...timesheet,
                isExpanded: false
            } as unknown as TimesheetContainer
            const parent = notUndefined(
                notUndefined(this.latestDatasource).find((t) => t.name === notUndefined(timesheet.parent).name)
            )
            const childIndexToReplace = notUndefined(parent.children).findIndex(
                (t) =>
                    t.employee.id === timesheet.employee.id && t.timesheetProject.id === timesheet.timesheetProject.id
            )
            notUndefined(parent.children).splice(childIndexToReplace, 1, cloneTimesheet)
        }
    }

    private async approveAndMapId(timesheet: Timesheet): Promise<Timesheet> {
        const resultTimesheet = await this.timesheetService.approveTimesheet(timesheet)
        timesheet.id = resultTimesheet.id

        return timesheet
    }

    private showConfirmDialog<T>(dialogRef: ComponentType<T> | TemplateRef<T>): MatDialogRef<T> {
        return this.dialogService.open(dialogRef)
    }

    private startEmployeeFilterSubscription(): void {
        this.subscriptions.add(
            this.employeeFilterService.employee$
                .pipe(filter((e) => !(e === undefined && this.filter.searchParams.employeeId === undefined)))
                .subscribe(async (employee: Employee | undefined) => {
                    this.filter.searchParams.employeeId = employee ? employee.id : undefined
                    this.filter.employeeId = this.filter.searchParams.employeeId
                    await this.fetchTimesheets()
                })
        )
    }

    private async fetchTimesheetsByLocalstorageCriteria(): Promise<void> {
        this.filter.searchParams = this.getTimesheetGenCriteria()
        await this.fetchTimesheets()
    }

    private getTimesheetGenCriteria(): TimesheetGenCriteria {
        const criteria = this.timekeeperService.getTimesheetTimeFrame()

        return new TimesheetGenCriteria(
            criteria.month,
            notUndefined(criteria.year),
            this.employeeFilterService.lastSelectedEmployee
                ? this.employeeFilterService.lastSelectedEmployee.id
                : undefined,
            undefined,
            undefined
        )
    }

    private async fetchTimesheets(): Promise<void> {
        this.loaderService.startLoading()
        try {
            let timesheetPaginatedList
            this.latestDatasource = undefined
            if (this.isOnlyNotInvoiced) {
                const searchParams = new TimesheetSearchCriteria()
                searchParams.month = this.filter.searchParams.month
                searchParams.year = this.filter.searchParams.year
                searchParams.employeeId = this.filter.searchParams.employeeId
                const result = await this.timesheetService.getNotInvoicedTimesheets(searchParams)
                timesheetPaginatedList = result ? result.items : []
            } else {
                timesheetPaginatedList = await this.timesheetService.getVirtualTimesheetsForMonth(
                    this.filter.searchParams
                )
            }
            if (notUndefined(timesheetPaginatedList).length === 0) {
                const msg = `Found 0 timesheets for parameters ${toText(this.filter.searchParams).toLowerCase()}`
                this.snackbarService.promtSnackBar(msg)
            }
            notUndefined(timesheetPaginatedList).forEach((timesheet) => {
                this.timesheetService.setTotalsForTimesheet(timesheet as TimesheetContainer)
            })
            this.latestDatasource = this.timesheetService.startGroupingTimesheets(
                timesheetPaginatedList as Array<TimesheetContainer>,
                this.filter.groupBy
            )
            if (this.isViewByTag) {
                this.setTotalsForViewByTag(this.latestDatasource)
            }
            this.datasourceSubject.next(this.latestDatasource)
        } catch (err) {
            this.snackbarService.handleError(this.prefix, err, true)
        } finally {
            this.loaderService.stopLoading()
        }
    }

    private setTotalsForViewByTag = (timesheetContainer: Array<TimesheetContainer>) => {
        if (timesheetContainer.length > 0) {
            timesheetContainer.forEach((t) => {
                if (t.children) {
                    this.setTotalsForViewByTag(t.children)
                } else {
                    this.timesheetService.setTotalsForTimesheet(t)
                }
            })
        }
    }
}
